import type { QuoteData, QuoteStep } from '../types/quote.types';
import { isVehicleDocumentIncomplete } from '../utils/vehicleDocument';
import {
    STEP_NEEDS,
    VEHICLE_NEEDS_REGISTRATION_DATE,
    PAYMENT_NEEDS_SETTLED_PREVIEW,
    PAYMENT_NEEDS_OFFERABLE_METHOD,
    PAYMENT_NEEDS_OFFERED_METHOD,
    PAYMENT_NEEDS_FUTURE_SERVICE_DATES,
    CONFIGURATION_NEEDS_CURRENT_PRICE,
    CONFIGURATION_NEEDS_PRICING,
    CONFIGURATION_NEEDS_REGENERATED_PLAN,
    configurationNeeds,
    nextBlockedMessage,
    completionBlockedMessage,
    type PaymentPreviewBlockReason,
} from '../utils/stepGate';
import { minimumConfiguredServices } from '@features/service-plans/utils/minimumConfiguredServices';
import { isValidPaymentSelection } from '../types/schedule.types';
import { isUnrecoverablePricingError } from '../utils/pricingErrors';
import type { BasketPricingState } from './useBasketPricing';

interface UseStepBlockReasonArgs {
    readonly quoteData: QuoteData;
    readonly stepOrder: readonly QuoteStep[];
    readonly vehicleDetailsBlocked: boolean;
    readonly paymentPreviewBlockReason: PaymentPreviewBlockReason | null;
    readonly pricing: BasketPricingState;
}

/** Current exactly when the shared basket-pricing query matches this basket and has settled
 *  with data: `isUpdating` also covers a background refetch over an otherwise-fresh entry. */
const pricingIsCurrent = (pricing: BasketPricingState): boolean =>
    pricing.matchesInput && !pricing.isStale && !pricing.isUpdating && pricing.data !== undefined;

/** Strict (gate): a basket is unpriced until its own price has fully settled, so Next/Complete
 *  never carry a stale or in-flight price to Summary. */
const pricingNeeds = (pricing: BasketPricingState): string | null => {
    if (pricing.notRequired) return null;
    if (pricing.error && pricing.matchesInput) {
        return isUnrecoverablePricingError(pricing.error)
            ? CONFIGURATION_NEEDS_REGENERATED_PLAN
            : CONFIGURATION_NEEDS_PRICING;
    }
    return pricingIsCurrent(pricing) ? null : CONFIGURATION_NEEDS_CURRENT_PRICE;
};

/** Issue #825, D6. What each step needs, as a noun phrase, for `stepBlockReason`'s `summary`
 *  branch — kept in step with that switch's own precedence so the two cannot disagree. */
const needsFor =
    (quoteData: QuoteData, pricing: BasketPricingState) =>
    (step: QuoteStep): string => {
        if (step === 'vehicle' && quoteData.selectedVehicle) {
            return VEHICLE_NEEDS_REGISTRATION_DATE;
        }
        if (step === 'configuration') {
            const min = minimumConfiguredServices(quoteData.selectedProduct);
            if (quoteData.configuredServices.length < min) {
                return configurationNeeds(min);
            }
            const pricingNeed = pricingNeeds(pricing);
            if (pricingNeed !== null) return pricingNeed;
        }
        return STEP_NEEDS[step];
    };

const buildStepBlockReason = (
    quoteData: QuoteData,
    stepOrder: readonly QuoteStep[],
    vehicleDetailsBlocked: boolean,
    paymentPreviewBlockReason: PaymentPreviewBlockReason | null,
    pricing: BasketPricingState,
): ((step: QuoteStep) => string | null) => {
    const needs = needsFor(quoteData, pricing);

    const stepBlockReason = (step: QuoteStep): string | null => {
        switch (step) {
            case 'dealer':
                return quoteData.selectedDealer ? null : nextBlockedMessage(STEP_NEEDS.dealer);

            case 'vehicle': {
                const vehicle = quoteData.selectedVehicle;
                if (!vehicle) return nextBlockedMessage(STEP_NEEDS.vehicle);

                // Imported, never re-implemented here; adds the `type !== 'vehicle'`
                // discriminator, since a raw derivative is exempt on the wire but not in the journey.
                if (isVehicleDocumentIncomplete(vehicle)) {
                    return nextBlockedMessage(VEHICLE_NEEDS_REGISTRATION_DATE);
                }

                // Session-scoped and false while the form is unmounted, so it can only add a
                // block the user can see, never carry a stale one to a later step.
                if (vehicleDetailsBlocked) {
                    return nextBlockedMessage(VEHICLE_NEEDS_REGISTRATION_DATE);
                }
                return null;
            }

            case 'product':
                return quoteData.selectedProduct ? null : nextBlockedMessage(STEP_NEEDS.product);
            case 'configuration': {
                // Hard coupling: this arm and `buildQuoteSnapshot()` in services/quoteSnapshot.ts
                // must change together, or a quote walks to Summary unsaved with no error shown.
                const min = minimumConfiguredServices(quoteData.selectedProduct);
                if (quoteData.configuredServices.length < min) {
                    return nextBlockedMessage(configurationNeeds(min));
                }
                // Stricter than the save's own pricing fetch: a looser gate would
                // let a stale price reach Summary. Retry copy needs an error for this basket.
                const pricingNeed = pricingNeeds(pricing);
                return pricingNeed === null ? null : nextBlockedMessage(pricingNeed);
            }
            case 'customer':
                return quoteData.selectedCustomer ? null : nextBlockedMessage(STEP_NEEDS.customer);
            case 'payment': {
                // Outranks the check below: a fresh all-unavailable quote has no
                // schedule to confirm, so the generic copy would misname the remedy.
                if (paymentPreviewBlockReason === 'all-methods-unavailable') {
                    return nextBlockedMessage(PAYMENT_NEEDS_OFFERABLE_METHOD);
                }
                // A settled, valid `paymentSelection` is required; a live,
                // in-progress preview never counts on its own.
                if (!isValidPaymentSelection(quoteData.paymentSelection)) {
                    return nextBlockedMessage(STEP_NEEDS.payment);
                }
                switch (paymentPreviewBlockReason) {
                    case null:
                        return null;
                    case 'method-unoffered':
                        return nextBlockedMessage(PAYMENT_NEEDS_OFFERED_METHOD);
                    case 'past-service-date':
                        return nextBlockedMessage(PAYMENT_NEEDS_FUTURE_SERVICE_DATES);
                    case 'unsettled':
                        return nextBlockedMessage(PAYMENT_NEEDS_SETTLED_PREVIEW);
                }
            }

            // eslint-disable-next-line no-fallthrough -- tsc proves the switch above exhaustive; ESLint's CFG cannot see the type narrowing.
            case 'summary': {
                // Same predicate as canCompleteQuote(), reported rather than returned as a
                // boolean; the earliest unmet precondition is the one to name.
                const failing = stepOrder.slice(0, -1).find((s) => stepBlockReason(s) !== null);
                if (!failing) return null;
                return completionBlockedMessage(failing, needs(failing));
            }
            default:
                return 'This step cannot be completed.';
        }
    };

    return stepBlockReason;
};

/** True when completion waits only on this basket's price settling, not on a missing step. */
const buildIsPricingPending = (
    stepOrder: readonly QuoteStep[],
    stepBlockReason: (step: QuoteStep) => string | null,
    quoteData: QuoteData,
    pricing: BasketPricingState,
): boolean =>
    quoteData.configuredServices.length >= minimumConfiguredServices(quoteData.selectedProduct) &&
    pricingNeeds(pricing) === CONFIGURATION_NEEDS_CURRENT_PRICE &&
    stepOrder.slice(0, -1).every((s) => s === 'configuration' || stepBlockReason(s) === null);

export interface StepBlockReasons {
    /** Strict: gates Next/Complete — a step is proceedable only once fully settled. `isStepValid`
     *  is defined in terms of this, so a blocked step always has a reason and vice versa. */
    readonly stepBlockReason: (step: QuoteStep) => string | null;
    /** Completion is blocked only by a basket price still loading or refetching. */
    readonly isPricingPending: boolean;
}

/** The strict per-step gate reason; `isStepValid` is defined in terms of it. */
export const useStepBlockReason = ({
    quoteData,
    stepOrder,
    vehicleDetailsBlocked,
    paymentPreviewBlockReason,
    pricing,
}: UseStepBlockReasonArgs): StepBlockReasons => {
    const stepBlockReason = buildStepBlockReason(
        quoteData,
        stepOrder,
        vehicleDetailsBlocked,
        paymentPreviewBlockReason,
        pricing,
    );
    return {
        stepBlockReason,
        isPricingPending: buildIsPricingPending(stepOrder, stepBlockReason, quoteData, pricing),
    };
};
